import { Routes, Route, RedirectFunction } from '@angular/router';
import { environment } from '../environments/environment';
import { authGuard } from './auth/core/auth.guard';
import { adminGuard } from './admin/core/auth/admin.guard';
import { tenantAuthGuard } from './rms/core/auth/tenant-auth.guard';
import { unsavedImportGuard } from './budget/features/import/unsaved-import.guard';
import { Home } from './pages/home/home';
import { Contact } from './pages/contact/contact';
import { Testimonials } from './pages/testimonials/testimonials';
import { Terms } from './pages/terms/terms';
import { Privacy } from './pages/privacy/privacy';
import { Disclaimer } from './pages/disclaimer/disclaimer';
import { DataDeletion } from './pages/data-deletion/data-deletion';
import { RefundPolicy } from './pages/refund-policy/refund-policy';
import { About } from './pages/about/about';
import { Learn } from './pages/learn/learn';
import { ArticleView } from './pages/learn/article-view/article-view';
import { ARTICLES } from './pages/learn/learn-content';
import { HomeLoanCalculator } from './pages/home-loan/home-loan-calculator';
import { HomeLoanPrepaymentCalculator } from './pages/home-loan-prepayment/home-loan-prepayment-calculator';
import { PersonalLoanCalculator } from './pages/personal-loan/personal-loan-calculator';
import { CarLoanCalculator } from './pages/car-loan/car-loan-calculator';
import { PpfCalculator } from './pages/ppf/ppf-calculator';
import { SsyCalculator } from './pages/ssy/ssy-calculator';
import { FdCalculator } from './pages/fd/fd-calculator';
import { RdCalculator } from './pages/rd/rd-calculator';
import { CagrCalculator } from './pages/cagr/cagr-calculator';
import { InflationCalculator } from './pages/inflation/inflation-calculator';
import { EpfCalculator } from './pages/epf/epf-calculator';
import { NpsCalculator } from './pages/nps/nps-calculator';
import { TermInsuranceCalculator } from './pages/term-insurance/term-insurance-calculator';
import { IncomeTaxCalculator } from './pages/income-tax/income-tax-calculator';
import { HraCalculator } from './pages/hra/hra-calculator';
import { TdsPropertySaleCalculator } from './pages/tds-property-sale/tds-property-sale-calculator';
import { SipCalculator } from './pages/sip/sip-calculator';
import { StepUpSipCalculator } from './pages/step-up-sip/step-up-sip-calculator';
import { SipVsStepUpSipCalculator } from './pages/sip-vs-step-up-sip/sip-vs-step-up-sip-calculator';
import { LumpsumCalculator } from './pages/lumpsum/lumpsum-calculator';
import { StpCalculator } from './pages/stp/stp-calculator';
import { SwpCalculator } from './pages/swp/swp-calculator';
import { FireCalculator } from './pages/fire/fire-calculator';
import { StampDutyKarnatakaCalculator } from './pages/stamp-duty-karnataka/stamp-duty-karnataka-calculator';
import { NotFound } from './pages/not-found/not-found';

/**
 * A plain `redirectTo: string` drops the original navigation's query params (e.g. `returnUrl`),
 * which silently breaks any old bookmarked `/reminders/login?returnUrl=...` link - the user logs
 * in successfully but lands on the homepage instead of back where they were headed.
 */
function redirectPreservingQueryParams(target: string): RedirectFunction {
  return ({ queryParams }) => {
    const query = new URLSearchParams(queryParams as Record<string, string>).toString();
    return query ? `${target}?${query}` : target;
  };
}

export const routes: Routes = [
  {
    path: '',
    component: Home,
    title: 'Financial Calculators for India | Every Rupee Spent',
    data: {
      description: 'Online calculators for Home Loan EMI, Personal Loan EMI, SIP, PPF, Income Tax and more — instant results, charts and amortization schedules.'
    }
  },
  {
    path: 'home-loan',
    component: HomeLoanCalculator,
    title: 'Home Loan EMI Calculator | Every Rupee Spent',
    data: {
      description: 'Online home loan EMI calculator with amortization schedule, prepayment planning and an AI advisor. See your EMI, interest and savings instantly.'
    }
  },
  {
    path: 'home-loan-prepayment',
    component: HomeLoanPrepaymentCalculator,
    title: 'Home Loan Prepayment Calculator | Every Rupee Spent',
    data: {
      description: 'Free home loan prepayment calculator for India. See how much interest you save and how many years you cut off your loan with a lump-sum or extra monthly payment — reduce tenure or reduce EMI.'
    }
  },
  {
    path: 'personal-loan',
    component: PersonalLoanCalculator,
    title: 'Personal Loan EMI Calculator | Every Rupee Spent',
    data: {
      description: 'Calculate your personal loan EMI, total interest and full amortization schedule instantly with our online personal loan calculator for India.'
    }
  },
  {
    path: 'car-loan',
    component: CarLoanCalculator,
    title: 'Car Loan EMI Calculator | Every Rupee Spent',
    data: {
      description: 'Calculate your car loan EMI, total interest and full amortization schedule instantly with our online car loan calculator for India.'
    }
  },
  {
    path: 'ppf',
    component: PpfCalculator,
    title: 'PPF Calculator - Maturity Value | Every Rupee Spent',
    data: {
      description: 'Calculate PPF maturity value, total interest earned and year-wise growth with our online PPF (Public Provident Fund) calculator for India.'
    }
  },
  {
    path: 'ssy',
    component: SsyCalculator,
    title: 'SSY Calculator - Maturity Value | Every Rupee Spent',
    data: {
      description: 'Calculate the maturity value of a Sukanya Samriddhi Yojana (SSY) account for your girl child with our online SSY calculator.'
    }
  },
  {
    path: 'fd',
    component: FdCalculator,
    title: 'FD Calculator - Fixed Deposit | Every Rupee Spent',
    data: {
      description: 'Calculate the maturity value and interest earned on a bank Fixed Deposit (FD) with our online FD calculator, compounded quarterly.'
    }
  },
  {
    path: 'rd',
    component: RdCalculator,
    title: 'RD Calculator - Recurring Deposit | Every Rupee Spent',
    data: {
      description: 'Calculate the maturity value and interest earned on a bank Recurring Deposit (RD) with our online RD calculator, compounded quarterly.'
    }
  },
  {
    path: 'cagr',
    component: CagrCalculator,
    title: 'CAGR Calculator - Growth Rate | Every Rupee Spent',
    data: {
      description: 'Calculate the Compound Annual Growth Rate (CAGR) of an investment from its initial value, final value and duration with our online CAGR calculator.',
      howToName: 'How to calculate CAGR',
      howToSteps: [
        { name: 'Enter the initial value', text: 'Enter the value of the investment at the start of the period.' },
        { name: 'Enter the final value', text: 'Enter the value of the investment at the end of the period.' },
        { name: 'Enter the number of years', text: 'Enter the number of years between the initial and final value.' },
        { name: 'View the CAGR', text: 'The calculator shows the Compound Annual Growth Rate — the smoothed annual growth rate between the two values.' }
      ]
    }
  },
  {
    path: 'inflation',
    component: InflationCalculator,
    title: 'Inflation Calculator - Future Cost | Every Rupee Spent',
    data: {
      description: 'Calculate the future cost of an item or the future purchasing power of your money, adjusted for inflation, with our online inflation calculator.'
    }
  },
  {
    path: 'epf',
    component: EpfCalculator,
    title: 'EPF Calculator - Provident Fund | Every Rupee Spent',
    data: {
      description: 'Calculate your EPF (Employee Provident Fund) maturity value at retirement, including employee and employer contributions, with our online EPF calculator.'
    }
  },
  {
    path: 'nps',
    component: NpsCalculator,
    title: 'NPS Calculator - Retirement Corpus | Every Rupee Spent',
    data: {
      description: 'Calculate your National Pension System (NPS) corpus at retirement, expected lumpsum withdrawal and monthly pension with our online NPS calculator.'
    }
  },
  {
    path: 'lumpsum',
    component: LumpsumCalculator,
    title: 'Lumpsum Investment Calculator | Every Rupee Spent',
    data: {
      description: 'Calculate the maturity value and wealth gained on a one-time lumpsum mutual fund investment with our online lumpsum calculator.'
    }
  },
  {
    path: 'sip',
    component: SipCalculator,
    title: 'SIP Calculator - Mutual Fund Returns | Every Rupee Spent',
    data: {
      description: 'Calculate mutual fund SIP returns, maturity value and wealth gained with our online SIP (Systematic Investment Plan) calculator.',
      howToName: 'How to calculate SIP returns',
      howToSteps: [
        { name: 'Enter your monthly investment', text: 'Enter the amount you plan to invest every month.' },
        { name: 'Enter the expected annual return', text: 'Enter the annual return rate you expect from the mutual fund.' },
        { name: 'Enter the investment duration', text: 'Enter how many years you plan to keep investing.' },
        { name: 'View the results', text: 'The calculator shows the maturity value, total amount invested and wealth gained.' }
      ]
    }
  },
  {
    path: 'step-up-sip',
    component: StepUpSipCalculator,
    title: 'Step-Up SIP Calculator - Mutual Fund Returns | Every Rupee Spent',
    data: {
      description: 'Calculate how much extra corpus a step-up (top-up) SIP builds versus a flat SIP, by increasing your monthly mutual fund investment by a fixed percentage every year.',
      howToName: 'How to calculate step-up SIP returns',
      howToSteps: [
        { name: 'Enter your starting monthly investment', text: 'Enter the amount you plan to invest every month in year 1.' },
        { name: 'Enter the annual step-up percentage', text: 'Enter how much your SIP amount should increase by, every year.' },
        { name: 'Enter the expected annual return', text: 'Enter the annual return rate you expect from the mutual fund.' },
        { name: 'Enter the investment duration', text: 'Enter how many years you plan to keep investing.' },
        { name: 'View the results', text: 'The calculator shows the maturity value, total amount invested and the extra corpus built versus a flat SIP.' }
      ]
    }
  },
  {
    path: 'sip-vs-step-up-sip',
    component: SipVsStepUpSipCalculator,
    title: 'SIP vs Step-Up SIP Calculator | Every Rupee Spent',
    data: {
      description: 'Compare a flat monthly SIP against a step-up SIP with the same starting amount, expected return and tenure — see the extra corpus a step-up builds, side by side.',
      howToName: 'How to compare a SIP against a step-up SIP',
      howToSteps: [
        { name: 'Enter your starting monthly investment', text: 'Enter the amount you plan to invest every month, used for both sides of the comparison.' },
        { name: 'Enter the annual step-up percentage', text: 'Enter how much the step-up SIP\'s installment should increase by, every year.' },
        { name: 'Enter the expected annual return', text: 'Enter the annual return rate you expect from the mutual fund, used for both sides.' },
        { name: 'Enter the investment duration', text: 'Enter how many years you plan to keep investing.' },
        { name: 'View the comparison', text: 'The calculator shows both maturity values side by side, plus the extra corpus the step-up SIP builds.' }
      ]
    }
  },
  {
    path: 'stp',
    component: StpCalculator,
    title: 'STP Calculator - Transfer Plan | Every Rupee Spent',
    data: {
      description: 'Calculate returns from a Systematic Transfer Plan (STP) between mutual funds with our online STP calculator.'
    }
  },
  {
    path: 'swp',
    component: SwpCalculator,
    title: 'SWP Calculator - Withdrawal Plan | Every Rupee Spent',
    data: {
      description: 'Plan your Systematic Withdrawal Plan (SWP), see how long your corpus lasts and total withdrawals with our online SWP calculator.'
    }
  },
  {
    path: 'fire',
    component: FireCalculator,
    title: 'FIRE Calculator - Retire Early | Every Rupee Spent',
    data: {
      description: 'Find your FIRE number and the age you can retire early, with year-wise projections and a shortfall action plan.'
    }
  },
  {
    path: 'term-insurance',
    component: TermInsuranceCalculator,
    title: 'Term Insurance Calculator | Every Rupee Spent',
    data: {
      description: 'Find out how much term life insurance cover you need based on your income, debts and future goals with our online term insurance calculator.'
    }
  },
  {
    path: 'income-tax',
    component: IncomeTaxCalculator,
    title: 'Income Tax Calculator FY 2025-26 | Every Rupee Spent',
    data: {
      description: 'Compare your income tax under the Old vs New regime for FY 2025-26 and find out which one saves you more, instantly.'
    }
  },
  {
    path: 'hra',
    component: HraCalculator,
    title: 'HRA Exemption Calculator | Every Rupee Spent',
    data: {
      description: 'Calculate your House Rent Allowance (HRA) tax exemption under Section 10(13A) with our online HRA calculator for the Old Tax Regime.'
    }
  },
  {
    path: 'tds-property-sale',
    component: TdsPropertySaleCalculator,
    title: 'TDS on Property Sale Calculator (Form 141/26QB) | Every Rupee Spent',
    data: {
      description: 'Calculate TDS on property sale under Section 393(1) of the Income-tax Act, 2025 (resident sellers, Form 141/Schedule B — the old Section 194-IA/Form 26QB) or Section 393(2) (NRI sellers), plus state-wise stamp duty and late fee/interest, instantly.'
    }
  },
  {
    path: 'stamp-duty-karnataka',
    component: StampDutyKarnatakaCalculator,
    title: 'Karnataka Stamp Duty & Registration Charges Calculator | Every Rupee Spent',
    data: {
      description: 'Free Karnataka stamp duty and registration charges calculator. Enter your property sale consideration value to see stamp duty, cess, surcharge and registration fee for BBMP/Corporation or Gram Panchayat areas.'
    }
  },
  {
    path: 'contact',
    component: Contact,
    title: 'Contact Us | Every Rupee Spent',
    data: {
      description: 'Get in touch with Every Rupee Spent — questions about our calculators, Reminders, Budget & Expense Tracker, Rental Management System or Rental Agreement Generator. We usually reply within 1–2 working days.'
    }
  },
  {
    path: 'testimonials',
    component: Testimonials,
    title: 'Testimonials | Every Rupee Spent',
    data: {
      description: 'Read what our users say about Every Rupee Spent\'s financial calculators and services, and share your own experience.'
    }
  },
  {
    path: 'terms',
    component: Terms,
    title: 'Terms of Service | Every Rupee Spent',
    data: {
      description: 'Terms of Service for Every Rupee Spent, covering our calculators, AI Advisor and Reminders subscriptions — scope, refunds, liability and our use of AI.'
    }
  },
  {
    path: 'privacy',
    component: Privacy,
    title: 'Privacy Policy | Every Rupee Spent',
    data: {
      description: 'How Every Rupee Spent collects, uses and protects your personal data, in line with India\'s DPDP Act 2023 — what we collect, who we share it with, how long we keep it and your rights.'
    }
  },
  {
    path: 'disclaimer',
    component: Disclaimer,
    title: 'Disclaimer | Every Rupee Spent',
    data: {
      description: 'Our calculators and AI Advisor are for information and education only, not financial, investment, tax or legal advice. Read the full Every Rupee Spent disclaimer.'
    }
  },
  {
    path: 'data-deletion',
    component: DataDeletion,
    title: 'Delete Your Account & Data | Every Rupee Spent',
    data: {
      description: 'How to permanently delete your Every Rupee Spent account and personal data — self-service from My Account, or by contacting us if you can\'t log in.'
    }
  },
  {
    path: 'refund-policy',
    component: RefundPolicy,
    title: 'Refund & Cancellation Policy | Every Rupee Spent',
    data: {
      description: 'How refunds and cancellations work for Every Rupee Spent\'s paid Services — the Rental Agreement Generator, and Reminders, Budget & Expense Tracker and Rental Management System subscriptions.'
    }
  },
  {
    path: 'about',
    component: About,
    title: 'About Us | Every Rupee Spent',
    data: {
      description: 'Every Rupee Spent builds free, accurate, India-first financial calculators, plus premium services — Reminders, Budget & Expense Tracker, Rental Management System and Rental Agreement Generator. Learn about our mission and methodology.'
    }
  },
  {
    path: 'learn',
    component: Learn,
    title: 'Money Guides for India | Every Rupee Spent',
    data: {
      description: 'Plain-language guides on Indian income tax, home loans, savings and retirement planning — each one links to the calculator you need to run your own numbers.'
    }
  },
  ...ARTICLES.map(article => ({
    path: `learn/${article.slug}`,
    component: ArticleView,
    title: article.metaTitle,
    data: {
      description: article.description,
      slug: article.slug,
      schemaType: 'article',
      datePublished: '2026-07-20',
      faqItems: article.faq
    }
  })),
  {
    path: 'login',
    loadComponent: () => import('./auth/features/login/login').then((m) => m.Login),
    title: 'Log in | Every Rupee Spent'
  },
  {
    path: 'register',
    loadComponent: () => import('./auth/features/register/register').then((m) => m.Register),
    title: 'Register | Every Rupee Spent'
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./auth/features/forgot-password/forgot-password').then((m) => m.ForgotPassword),
    title: 'Forgot password | Every Rupee Spent'
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./auth/features/reset-password/reset-password').then((m) => m.ResetPassword),
    title: 'Reset password | Every Rupee Spent'
  },
  // Kept for old bookmarks/links - login/register used to live under the Reminders module
  // before they were extracted into the shared top-level auth module.
  { path: 'reminders/login', redirectTo: redirectPreservingQueryParams('/login') },
  { path: 'reminders/register', redirectTo: redirectPreservingQueryParams('/register') },
  {
    path: 'reminders',
    loadComponent: () => import('./reminders/shared/shell/shell').then((m) => m.Shell),
    canActivate: [authGuard],
    title: 'Reminders | Every Rupee Spent',
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', loadChildren: () => import('./reminders/features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES) },
      { path: 'list', loadComponent: () => import('./reminders/features/reminders/reminder-list').then((m) => m.ReminderList) },
      { path: 'calendar', loadComponent: () => import('./reminders/features/calendar/calendar').then((m) => m.Calendar) },
      { path: 'categories', loadComponent: () => import('./reminders/features/categories/category-list').then((m) => m.CategoryList) },
      { path: 'settings', loadComponent: () => import('./reminders/features/settings/settings').then((m) => m.Settings) },
      { path: 'billing', loadComponent: () => import('./reminders/features/billing/billing').then((m) => m.Billing) }
    ]
  },
  {
    path: 'budget',
    loadComponent: () => import('./budget/shared/shell/shell').then((m) => m.Shell),
    canActivate: [authGuard],
    title: 'Budget & Expense Tracker | Every Rupee Spent',
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', loadChildren: () => import('./budget/features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES) },
      { path: 'budgets', loadComponent: () => import('./budget/features/budgets/budgets').then((m) => m.Budgets) },
      { path: 'expenses', loadComponent: () => import('./budget/features/expenses/expenses').then((m) => m.Expenses) },
      {
        path: 'import',
        loadComponent: () => import('./budget/features/import/import').then((m) => m.Import),
        canDeactivate: [unsavedImportGuard],
      },
      { path: 'categories', loadComponent: () => import('./budget/features/categories/category-list').then((m) => m.CategoryList) },
      { path: 'reports', loadChildren: () => import('./budget/features/reports/reports.routes').then((m) => m.REPORTS_ROUTES) },
      { path: 'billing', loadComponent: () => import('./budget/features/billing/billing').then((m) => m.Billing) },
      { path: 'settings', loadComponent: () => import('./budget/features/settings/settings').then((m) => m.Settings) }
    ]
  },
  {
    // Standalone module (src/app/todo/) — free tier stores up to 5 to-dos, ₹500/year Premium
    // raises that to 100. Reuses the shared site login: authGuard sends signed-out users to
    // /login?returnUrl=/todo.
    path: 'todo',
    loadComponent: () => import('./todo/features/todos/todo-list').then((m) => m.TodoList),
    canActivate: [authGuard],
    title: 'To-Do | Every Rupee Spent',
  },
  {
    path: 'todo/billing',
    loadComponent: () => import('./todo/features/billing/billing').then((m) => m.Billing),
    canActivate: [authGuard],
    title: 'To-Do Premium | Every Rupee Spent',
  },
  {
    // Standalone module (src/app/account/), shared by every authenticated feature —
    // Reminders, Budget, RMS landlord, Admin all link here for email + logout instead of
    // each owning its own copy. Reuses the same shared site login as everything else:
    // authGuard sends signed-out users to /login?returnUrl=/my-account.
    path: 'my-account',
    loadComponent: () => import('./account/features/my-account/my-account').then((m) => m.MyAccount),
    canActivate: [authGuard],
    title: 'My Account | Every Rupee Spent',
  },
  {
    // Standalone module (src/app/feedback/), shared by every authenticated feature —
    // Reminders, Budget, RMS all link here instead of owning their own copy. Reuses the
    // same shared site login as everything else: authGuard sends signed-out users to
    // /login?returnUrl=/feedback.
    path: 'feedback',
    loadComponent: () => import('./feedback/features/feedback/feedback').then((m) => m.FeedbackPage),
    canActivate: [authGuard],
    title: 'Feedback | Every Rupee Spent',
  },
  // Rental Agreement Generator - standalone module (src/app/rental-agreement/), no RMS account
  // needed. Gated behind environment.features.rentalAgreementEnabled, mirroring the backend's
  // app.rental-agreement.enabled @ConditionalOnProperty gate on RentalAgreementController/
  // RentalAgreementWebhookController (see PENDING_FEATURES.md #3). Reuses the shared site login:
  // authGuard sends signed-out users to /login?returnUrl=/rental-agreement.
  ...(environment.features.rentalAgreementEnabled ? [{
    path: 'rental-agreement',
    loadComponent: () => import('./rental-agreement/features/generate/generate').then((m) => m.Generate),
    canActivate: [authGuard],
    title: 'Rental Agreement Generator - Karnataka | Every Rupee Spent',
    data: {
      description: 'Generate a legally drafted, e-Stamped and e-signed 11-month rental agreement for Karnataka. ₹300 flat for the draft, plus stamp duty and e-stamp/e-sign handling.',
    },
  } as Route] : []),
  // RMS routes are gated behind environment.features.rmsEnabled (mirrors the backend's
  // app.rms.enabled @ConditionalOnProperty gate on every RMS controller/scheduler, see
  // PENDING_FEATURES.md #1) - when off, none of these paths are registered and the trailing
  // wildcard route below redirects '**' to '/'.
  ...(environment.features.rmsEnabled ? [
    {
      // Landlords authenticate through the same shared site login as Reminders — authGuard
      // (reused, not duplicated) sends them to /login?returnUrl=/rms if signed out.
      path: 'rms',
      loadComponent: () => import('./rms/layouts/landlord-layout/landlord-layout').then((m) => m.LandlordLayout),
      canActivate: [authGuard],
      title: 'Rental Management System | Every Rupee Spent',
      children: [
        { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        { path: 'dashboard', loadComponent: () => import('./rms/features/landlord/dashboard/dashboard').then((m) => m.Dashboard) },
        { path: 'properties', loadComponent: () => import('./rms/features/landlord/properties/properties-list').then((m) => m.PropertiesList) },
        { path: 'properties/:id', loadComponent: () => import('./rms/features/landlord/properties/property-detail').then((m) => m.PropertyDetail) },
        { path: 'tenants', loadComponent: () => import('./rms/features/landlord/tenants/tenants-list').then((m) => m.TenantsList) },
        { path: 'tenants/:id', loadComponent: () => import('./rms/features/landlord/tenants/tenant-detail').then((m) => m.TenantDetail) },
        { path: 'leases', loadComponent: () => import('./rms/features/landlord/leases/leases-list').then((m) => m.LeasesList) },
        { path: 'leases/:id', loadComponent: () => import('./rms/features/landlord/leases/lease-detail').then((m) => m.LeaseDetail) },
        { path: 'rent', loadComponent: () => import('./rms/features/landlord/rent/rent').then((m) => m.Rent) },
        { path: 'maintenance', loadComponent: () => import('./rms/features/landlord/maintenance/maintenance').then((m) => m.Maintenance) },
        { path: 'work-orders', loadComponent: () => import('./rms/features/landlord/work-orders/work-orders').then((m) => m.WorkOrders) },
        { path: 'utility-bills', loadComponent: () => import('./rms/features/landlord/utility-bills/utility-bills').then((m) => m.UtilityBills) },
        { path: 'property-tax', loadComponent: () => import('./rms/features/landlord/property-tax/property-tax').then((m) => m.PropertyTax) },
        { path: 'agreements', loadComponent: () => import('./rms/features/landlord/agreements/agreements').then((m) => m.Agreements) },
        { path: 'billing', loadComponent: () => import('./rms/features/landlord/billing/billing').then((m) => m.Billing) },
        { path: 'settings', loadComponent: () => import('./rms/features/landlord/settings/settings').then((m) => m.Settings) },
        { path: 'reports', loadComponent: () => import('./rms/features/landlord/reports/reports').then((m) => m.Reports) },
      ]
    },
    {
      path: 'rms/tenant/login',
      loadComponent: () => import('./rms/features/tenant/auth/tenant-login').then((m) => m.TenantLogin),
      title: 'Tenant Login | Every Rupee Spent'
    },
    {
      // Tenants have no site account — phone-OTP only, via the self-contained tenant-auth stack.
      path: 'rms/tenant',
      loadComponent: () => import('./rms/layouts/tenant-layout/tenant-layout').then((m) => m.TenantLayout),
      canActivate: [tenantAuthGuard],
      title: 'My Rental | Every Rupee Spent',
      children: [
        { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        { path: 'dashboard', loadComponent: () => import('./rms/features/tenant/dashboard/tenant-dashboard').then((m) => m.TenantDashboard) },
        { path: 'my-lease', loadComponent: () => import('./rms/features/tenant/my-lease/my-lease').then((m) => m.MyLease) },
        { path: 'maintenance', loadComponent: () => import('./rms/features/tenant/maintenance/tenant-maintenance').then((m) => m.TenantMaintenance) },
        { path: 'receipts', loadComponent: () => import('./rms/features/tenant/receipts/receipts').then((m) => m.Receipts) },
        { path: 'passport', loadComponent: () => import('./rms/features/tenant/passport/tenant-passport').then((m) => m.TenantPassport) },
        { path: 'account', loadComponent: () => import('./rms/features/tenant/account/tenant-account').then((m) => m.TenantAccount) },
      ]
    },
    {
      // Public rental-passport share view — matches the backend's permitAll /api/rms/passport/public/** route.
      path: 'rms/passport/:token',
      loadComponent: () => import('./rms/features/tenant/passport/public-passport').then((m) => m.PublicPassport),
      title: 'Rental Passport | Every Rupee Spent'
    },
  ] as Route[] : []),
  {
    // Admin-only. No public nav link - navigate to /admin directly.
    path: 'admin',
    loadComponent: () => import('./admin/shared/shell/shell').then((m) => m.Shell),
    canActivate: [adminGuard],
    title: 'Admin Console | Every Rupee Spent',
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      { path: 'overview', loadChildren: () => import('./admin/features/overview/overview.routes').then((m) => m.OVERVIEW_ROUTES) },
      { path: 'users', loadComponent: () => import('./admin/features/users/users').then((m) => m.Users) },
      { path: 'activity', loadComponent: () => import('./admin/features/activity/activity').then((m) => m.Activity) },
      { path: 'testimonials', loadComponent: () => import('./admin/features/testimonials/testimonials').then((m) => m.Testimonials) },
      { path: 'feedback', loadComponent: () => import('./admin/features/feedback/feedback').then((m) => m.AdminFeedback) },
      { path: 'payments', loadComponent: () => import('./admin/features/payments/payments').then((m) => m.Payments) },
      { path: 'settings', loadComponent: () => import('./admin/features/settings/settings').then((m) => m.Settings) },
    ]
  },
  {
    // A real, prerenderable URL (unlike the `**` wildcard below, which can't be enumerated as a
    // static file) so the build emits an actual dist/browser/404/index.html. Nginx points its
    // `error_page 404` directive at this file so broken URLs get a genuine HTTP 404 status even
    // though the site is served as static files with no live SSR server in production — see the
    // "Custom 404 page" step in DEPLOY_RUNBOOK.md.
    path: '404',
    component: NotFound,
    title: 'Page Not Found | Every Rupee Spent'
  },
  {
    // A silent `redirectTo: ''` used to send every broken/mistyped URL to the homepage with an
    // HTTP 200 — a "soft 404" that Google's SEO guide explicitly warns against (it wastes crawl
    // budget and can get junk URLs indexed as duplicates of the homepage). This renders the same
    // not-found page client-side for anyone who reaches a bad URL via in-app navigation.
    path: '**',
    component: NotFound,
    title: 'Page Not Found | Every Rupee Spent'
  }
];
